import { useState, useEffect } from 'react';
import { api } from '../lib/api.js';

// ─── Holiday list (Settings → Holidays) ────────────────────────────────────
export function HolidayListView({ currentUser, readOnly = false }) {
  const headers = { 'x-user-id': String(currentUser.id) };
  const [rows, setRows] = useState([]);
  const [locations, setLocations] = useState([]);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ holiday_date: '', name: '', pays_1_5x: true, tips_split_location_ids: [] });
  const [err, setErr] = useState('');

  useEffect(() => {
    if (readOnly) return;
    api('GET', '/api/locations', null, headers).then(setLocations).catch(() => {});
  }, [readOnly]);

  async function load() {
    try {
      // Owners see the admin route (full CRUD); everyone else hits the
      // public read-only route added for the staff-facing Holidays view.
      const path = readOnly ? '/api/holidays' : '/api/admin/payroll/holidays';
      const r = await api('GET', path, null, headers);
      setRows(r.holidays || []);
    } catch (e) { setErr(e.message || String(e)); }
  }
  useEffect(() => { load(); }, []);

  async function add(e) {
    e.preventDefault();
    if (!form.holiday_date || !form.name.trim()) return;
    setBusy(true); setErr('');
    try {
      await api('POST', '/api/admin/payroll/holidays', form, headers);
      setForm({ holiday_date: '', name: '', pays_1_5x: true, tips_split_location_ids: [] });
      load();
    } catch (e) { setErr(e.message || String(e)); }
    finally { setBusy(false); }
  }
  async function toggle(h) {
    try {
      await api('PUT', `/api/admin/payroll/holidays/${h.id}`, { pays_1_5x: !h.pays_1_5x }, headers);
      load();
    } catch (e) { alert(e.message || e); }
  }
  async function toggleTipsSplitLocation(h, locId) {
    const cur = Array.isArray(h.tips_split_location_ids) ? h.tips_split_location_ids : [];
    const next = cur.includes(locId) ? cur.filter(x => x !== locId) : [...cur, locId];
    try {
      await api('PUT', `/api/admin/payroll/holidays/${h.id}`, { tips_split_location_ids: next }, headers);
      load();
    } catch (e) { alert(e.message || e); }
  }
  async function del(h) {
    if (!confirm(`Delete ${h.name} (${h.holiday_date})?`)) return;
    try {
      await api('DELETE', `/api/admin/payroll/holidays/${h.id}`, null, headers);
      load();
    } catch (e) { alert(e.message || e); }
  }

  return (
    <div className="space-y-4">
      <div className="card">
        <div className="text-base font-semibold mb-1">Holidays</div>
        <div className="text-xs text-gray-500 mb-3">
          {readOnly
            ? 'Days the company pays at the higher rate (1.5×) appear with a green check below.'
            : 'Holidays with the 1.5× flag on reclassify regular hours as holiday pay (paid at 1.5×). Enter the date you actually pay the holiday on — Monarch does not auto-shift to observed days.'}
        </div>
        {!readOnly && (
          <form onSubmit={add} style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <label style={{ fontSize: 12, fontWeight: 600 }}>
              Date
              <input type="date" value={form.holiday_date}
                onChange={(e) => setForm({ ...form, holiday_date: e.target.value })}
                style={{ display: 'block', padding: 8, border: '1px solid #d0d4d9', borderRadius: 6, fontFamily: 'inherit' }} required />
            </label>
            <label style={{ fontSize: 12, fontWeight: 600, flex: 1, minWidth: 200 }}>
              Name
              <input type="text" value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="e.g. Thanksgiving"
                style={{ display: 'block', width: '100%', padding: 8, border: '1px solid #d0d4d9', borderRadius: 6, fontFamily: 'inherit' }} required />
            </label>
            <label style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
              <input type="checkbox" checked={form.pays_1_5x}
                onChange={(e) => setForm({ ...form, pays_1_5x: e.target.checked })} />
              Pays 1.5×
            </label>
            <button type="submit" disabled={busy} className="btn-primary">Add</button>
            {locations.length > 0 && (
              <div style={{ flexBasis: '100%', fontSize: 12, marginTop: 6 }}>
                <div style={{ fontWeight: 600, color: '#4b4636', marginBottom: 4 }}>
                  Split tips by day at
                  <span style={{ fontWeight: 400, color: '#9e9e9e', marginLeft: 6 }}>
                    (per location — leave all unchecked for the normal weekly pool)
                  </span>
                </div>
                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                  {locations.map(l => (
                    <label key={l.id} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12 }}>
                      <input type="checkbox"
                        checked={form.tips_split_location_ids.includes(l.id)}
                        onChange={(e) => {
                          const cur = form.tips_split_location_ids;
                          const next = e.target.checked
                            ? [...cur, l.id]
                            : cur.filter(x => x !== l.id);
                          setForm({ ...form, tips_split_location_ids: next });
                        }} />
                      {l.name}
                    </label>
                  ))}
                </div>
              </div>
            )}
          </form>
        )}
        {err && <div className="text-xs text-red-700 mt-2">{err}</div>}
      </div>

      <div className="card">
        {rows.length === 0 ? (
          <div className="text-sm text-gray-500">No holidays added yet.</div>
        ) : (
          <table style={{ width: '100%', fontSize: 13 }}>
            <thead>
              <tr style={{ textAlign: 'left', borderBottom: '1px solid #e0e0e0' }}>
                <th style={{ padding: 6 }}>Date</th>
                <th style={{ padding: 6 }}>Name</th>
                <th style={{ padding: 6 }}>Pays 1.5×</th>
                <th style={{ padding: 6 }}>Split tips by day</th>
                {!readOnly && <th></th>}
              </tr>
            </thead>
            <tbody>
              {rows.map((h) => (
                <tr key={h.id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                  <td style={{ padding: 6 }}>{String(h.holiday_date).slice(0, 10)}</td>
                  <td style={{ padding: 6 }}>{h.name}</td>
                  <td style={{ padding: 6 }}>
                    {readOnly ? (
                      <span style={{ color: h.pays_1_5x ? '#15803d' : '#9e9e9e' }}>
                        {h.pays_1_5x ? '✓ 1.5×' : '—'}
                      </span>
                    ) : (
                      <label>
                        <input type="checkbox" checked={!!h.pays_1_5x} onChange={() => toggle(h)} />{' '}
                        {h.pays_1_5x ? 'Yes' : 'No'}
                      </label>
                    )}
                  </td>
                  <td style={{ padding: 6 }}>
                    {readOnly ? (
                      (h.tips_split_location_ids && h.tips_split_location_ids.length > 0) ? (
                        <span style={{ color: '#15803d' }}>
                          ✓ {h.tips_split_location_ids.length} loc
                        </span>
                      ) : (
                        <span style={{ color: '#9e9e9e' }}>—</span>
                      )
                    ) : (
                      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        {locations.map(l => {
                          const on = Array.isArray(h.tips_split_location_ids) && h.tips_split_location_ids.includes(l.id);
                          return (
                            <label key={l.id} style={{ fontSize: 11, display: 'flex', alignItems: 'center', gap: 3 }}>
                              <input type="checkbox" checked={on}
                                onChange={() => toggleTipsSplitLocation(h, l.id)} />
                              {l.name}
                            </label>
                          );
                        })}
                      </div>
                    )}
                  </td>
                  {!readOnly && (
                    <td style={{ padding: 6, textAlign: 'right' }}>
                      <button onClick={() => del(h)} className="btn-secondary text-xs">Delete</button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
